import React, { useState } from 'react';
import { authenticatedFetch, setToken } from '../utils/auth';
import './Login.css';

const Login = ({ onLoginSuccess, onClose }) => {
  const [isLogin, setIsLogin] = useState(true); // true: 로그인, false: 회원가입
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nickname: '',
    name: '',
    // 모의투자 (기본값)
    paper_app_key: '',
    paper_app_secret: '',
    paper_account_no: '',
    // 실전투자
    real_app_key: '',
    real_app_secret: '',
    real_account_no: '',
    is_paper_trading: true // 기본값: 모의투자
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const getAuthApiBase = () => {
    const envBase = process.env.REACT_APP_TRADING_API_BASE;
    const host = window.location.hostname;
    const isTradingHost = host === 'trading.star612.net';
  const isTradingPath =
    window.location.pathname.startsWith('/trading') ||
    window.location.pathname.startsWith('/stockai');
    
    if (envBase) {
      return { isTrading: true, base: envBase };
    }
    
    if (isTradingHost || isTradingPath) {
      const base = isTradingPath
        ? `${window.location.origin}/trading`
        : window.location.origin;
      return { isTrading: true, base };
    }
    
    return { isTrading: false, base: window.location.origin };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        // 로그인 - trading-service 또는 star612.net에 맞게 분기
        const { isTrading, base } = getAuthApiBase();
        const apiUrl = isTrading
          ? `${base}/api/auth/login`
          : `${base}/api/auth/star612/login`;
        
        console.log('🔐 로그인 시도:', { apiUrl, email: formData.email });
        
        let response;
        try {
          response = await authenticatedFetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: formData.email,
              password: formData.password,
            }),
            timeout: 30000,
            skipAuthHandling: true
          });
        } catch (fetchError) {
          // 네트워크 에러 (서버 연결 실패, 타임아웃 등)
          if (fetchError.name === 'AbortError' || fetchError.name === 'TimeoutError') {
            setError('요청 시간이 초과되었습니다. 서버 상태를 확인해주세요.');
          } else if (fetchError.name === 'TypeError' && fetchError.message.includes('Failed to fetch')) {
            setError('서버에 연결할 수 없습니다. 네트워크 연결과 서버 상태를 확인해주세요.');
          } else {
            setError(`연결 오류: ${fetchError.message}`);
          }
          console.error('❌ 네트워크 에러:', fetchError);
          return;
        }

        // 502, 503 에러 처리 (서버 연결 오류)
        if (response.status === 502) {
          setError('서버 연결 오류 (502). trading-service가 실행 중인지 확인해주세요.');
          console.error('❌ 502 Bad Gateway:', {
            apiUrl,
            status: response.status,
            statusText: response.statusText
          });
          return;
        }
        
        if (response.status === 503) {
          setError('서비스가 일시적으로 사용할 수 없습니다 (503). 잠시 후 다시 시도해주세요.');
          console.error('❌ 503 Service Unavailable:', {
            apiUrl,
            status: response.status,
            statusText: response.statusText
          });
          return;
        }

        // Content-Type 확인 후 JSON 파싱
        const contentType = response.headers.get('content-type');
        let data;
        if (contentType && contentType.includes('application/json')) {
          try {
            data = await response.json();
          } catch (jsonError) {
            console.error('❌ JSON 파싱 실패:', jsonError);
            setError('서버 응답 형식 오류가 발생했습니다.');
            return;
          }
        } else {
          // JSON이 아닌 경우 (HTML 에러 페이지 등)
          const textResponse = await response.text();
          console.error('❌ 비JSON 응답:', {
            status: response.status,
            contentType,
            preview: textResponse.substring(0, 200)
          });
          setError('서버 오류가 발생했습니다. 관리자에게 문의해주세요.');
          return;
        }

        if (response.ok) {
          setToken(data.token);
          console.log('✅ 로그인 성공:', {
            userId: data.user?.id,
            email: data.user?.email,
            nickname: data.user?.nickname
          });
          if (onLoginSuccess) {
            onLoginSuccess(data.user, data.token);
          }
          if (onClose) {
            onClose();
          }
        } else {
          // 에러 응답 처리 (401: 자격 증명 오류 또는 거래 서비스 미가입 계정)
          const errorMessage = data.error || data.message || '로그인 실패';
          const friendlyMessage = response.status === 401
            ? '이메일 또는 비밀번호를 확인해 주세요. 메인(star612.net)에서 가입한 계정은 거래 서비스와 별도입니다. 거래 서비스를 이용하려면 여기서 회원가입을 진행해 주세요.'
            : errorMessage;
          setError(friendlyMessage);
          console.error('❌ 로그인 실패:', {
            status: response.status,
            error: errorMessage,
            data
          });
        }
      } else {
        // 회원가입 - trading-service 또는 star612.net에 맞게 분기
        const { isTrading, base } = getAuthApiBase();
        const apiUrl = isTrading
          ? `${base}/api/auth/register`
          : `${base}/api/auth/star612/register`;
        
        console.log('📝 회원가입 시도:', { apiUrl, email: formData.email });
        
        let response;
        try {
          response = await authenticatedFetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: formData.email,
              password: formData.password,
              nickname: formData.nickname,
              name: formData.name,
              services: ['trading'],
              trading: {
                // 기본값: 모의투자
                is_paper_trading: formData.is_paper_trading !== false, // 기본값: true
                // 모의투자 설정
                paper_app_key: formData.paper_app_key,
                paper_app_secret: formData.paper_app_secret,
                paper_account_no: formData.paper_account_stock && formData.paper_prod ? `${formData.paper_account_stock}${formData.paper_prod}` : formData.paper_account_no,
                paper_hts_id: formData.paper_hts_id,
                paper_account_stock: formData.paper_account_stock,
                paper_account_future: formData.paper_account_future,
                paper_prod: formData.paper_prod,
                // 실전투자 설정
                real_app_key: formData.real_app_key,
                real_app_secret: formData.real_app_secret,
                real_account_no: formData.real_account_stock && formData.real_prod ? `${formData.real_account_stock}${formData.real_prod}` : formData.real_account_no,
                real_hts_id: formData.real_hts_id,
                real_account_stock: formData.real_account_stock,
                real_account_future: formData.real_account_future,
                real_prod: formData.real_prod,
                // 현재 사용 중인 키 (기본값: 모의투자)
                key_value_1: formData.paper_app_key,
                key_value_2: formData.paper_app_secret,
                account_no: formData.paper_account_stock && formData.paper_prod ? `${formData.paper_account_stock}${formData.paper_prod}` : formData.paper_account_no,
                hts_id: formData.paper_hts_id,
                account_stock: formData.paper_account_stock,
                account_future: formData.paper_account_future,
                account_prod: formData.paper_prod,
                settings: {}
              }
            }),
            timeout: 30000,
            skipAuthHandling: true
          });
        } catch (fetchError) {
          // 네트워크 에러 (서버 연결 실패, 타임아웃 등)
          if (fetchError.name === 'AbortError' || fetchError.name === 'TimeoutError') {
            setError('요청 시간이 초과되었습니다. 서버 상태를 확인해주세요.');
          } else if (fetchError.name === 'TypeError' && fetchError.message.includes('Failed to fetch')) {
            setError('서버에 연결할 수 없습니다. 네트워크 연결과 서버 상태를 확인해주세요.');
          } else {
            setError(`연결 오류: ${fetchError.message}`);
          }
          console.error('❌ 네트워크 에러:', fetchError);
          return;
        }

        // 502, 503 에러 처리 (서버 연결 오류)
        if (response.status === 502) {
          setError('서버 연결 오류 (502). trading-service가 실행 중인지 확인해주세요.');
          console.error('❌ 502 Bad Gateway:', {
            apiUrl,
            status: response.status,
            statusText: response.statusText
          });
          return;
        }
        
        if (response.status === 503) {
          setError('서비스가 일시적으로 사용할 수 없습니다 (503). 잠시 후 다시 시도해주세요.');
          console.error('❌ 503 Service Unavailable:', {
            apiUrl,
            status: response.status,
            statusText: response.statusText
          });
          return;
        }

        // Content-Type 확인 후 JSON 파싱
        const contentType = response.headers.get('content-type');
        let data;
        if (contentType && contentType.includes('application/json')) {
          try {
            data = await response.json();
          } catch (jsonError) {
            console.error('❌ JSON 파싱 실패:', jsonError);
            setError('서버 응답 형식 오류가 발생했습니다.');
            return;
          }
        } else {
          // JSON이 아닌 경우 (HTML 에러 페이지 등)
          const textResponse = await response.text();
          console.error('❌ 비JSON 응답:', {
            status: response.status,
            contentType,
            preview: textResponse.substring(0, 200)
          });
          setError('서버 오류가 발생했습니다. 관리자에게 문의해주세요.');
          return;
        }

        if (response.ok) {
          setToken(data.token);
          console.log('✅ 회원가입 성공:', {
            userId: data.user?.id,
            email: data.user?.email,
            nickname: data.user?.nickname
          });
          if (onLoginSuccess) {
            onLoginSuccess(data.user, data.token);
          }
          if (onClose) {
            onClose();
          }
        } else {
          // 409 에러는 이메일 중복
          if (response.status === 409) {
            setError('이미 등록된 이메일입니다. 로그인해주세요.');
            // 로그인 탭으로 전환
            setTimeout(() => {
              setIsLogin(true);
            }, 2000);
          } else {
            // 검증 에러 처리
            if (data.errors && Array.isArray(data.errors)) {
              const errorMessages = data.errors.map(e => e.msg || e.message).join(', ');
              setError(errorMessages || data.error || '회원가입 실패');
            } else {
              setError(data.error || data.message || '회원가입 실패');
            }
            console.error('❌ 회원가입 실패:', {
              status: response.status,
              error: data.error,
              errors: data.errors,
              data
            });
          }
        }
      }
    } catch (err) {
      // 예상치 못한 에러 처리
      console.error('❌ 예상치 못한 에러:', err);
      if (err.name === 'AbortError' || err.name === 'TimeoutError') {
        setError('요청 시간이 초과되었습니다. 서버 상태를 확인해주세요.');
      } else if (err.message) {
        setError(`오류 발생: ${err.message}`);
      } else {
        setError('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // 모달 닫기 전에 상태 초기화
    setError('');
    setLoading(false);
    // 폼 데이터는 유지 (사용자가 다시 열었을 때 편의성)
    if (onClose) {
      console.log('🔒 모달 닫기 호출');
      onClose();
    } else {
      console.warn('⚠️ onClose prop이 없습니다');
    }
  };

  return (
    <div className="login-overlay" onClick={handleClose}>
      <div className="login-modal" onClick={(e) => e.stopPropagation()}>
        <div className="login-header">
          <h2>{isLogin ? '로그인' : '회원가입'}</h2>
          <button 
            className="login-close" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('🔒 닫기 버튼 클릭');
              handleClose(e);
            }}
            type="button"
            aria-label="닫기"
            style={{
              background: 'none',
              border: 'none',
              fontSize: '30px',
              color: '#999',
              cursor: 'pointer',
              padding: '0',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'color 0.3s',
              zIndex: 1001,
              position: 'relative'
            }}
            onMouseEnter={(e) => e.target.style.color = '#333'}
            onMouseLeave={(e) => e.target.style.color = '#999'}
          >
            ×
          </button>
        </div>

        <div className="login-tabs">
          <button
            className={isLogin ? 'active' : ''}
            onClick={() => {
              setIsLogin(true);
              setError('');
            }}
          >
            로그인
          </button>
          <button
            className={!isLogin ? 'active' : ''}
            onClick={() => {
              setIsLogin(false);
              setError('');
            }}
          >
            회원가입
          </button>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="login-error">
              {error}
            </div>
          )}

          <div className="form-group">
            <label>이메일</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="example@email.com"
            />
          </div>

          <div className="form-group">
            <label>비밀번호</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength="6"
              placeholder="최소 6자 이상"
            />
          </div>

          {!isLogin && (
            <>
              <div className="form-group">
                <label>별명 (필수)</label>
                <input
                  type="text"
                  name="nickname"
                  value={formData.nickname}
                  onChange={handleChange}
                  required
                  placeholder="사용자 별명"
                  maxLength="50"
                />
                <small>서비스에서 표시될 별명입니다</small>
              </div>

              <div className="form-group">
                <label>이름 (실명) <span style={{color: 'red'}}>*</span></label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="실명을 입력하세요 (주식거래 필수)"
                  maxLength="50"
                />
                <small style={{color: 'red'}}>주식거래 서비스 이용을 위해 실명이 필수입니다</small>
              </div>

              <div className="form-divider">
                <h3>한국투자증권 API 설정</h3>
                <p className="form-hint">한투 Open API 서비스 신청 후 발급받은 키를 입력하세요</p>
              </div>

              <div className="form-group">
                <label>한투 API Key (App Key)</label>
                <input
                  type="text"
                  name="kis_app_key"
                  value={formData.kis_app_key}
                  onChange={handleChange}
                  placeholder="한투 Open API App Key"
                />
              </div>

              <div className="form-group">
                <label>한투 API Secret (App Secret)</label>
                <input
                  type="password"
                  name="kis_app_secret"
                  value={formData.kis_app_secret}
                  onChange={handleChange}
                  placeholder="한투 Open API App Secret"
                />
              </div>

              <div className="form-group">
                <label>계좌번호</label>
                <input
                  type="text"
                  name="account_no"
                  value={formData.account_no}
                  onChange={handleChange}
                  placeholder="계좌번호 10자리 (예: 1234567801)"
                  maxLength="10"
                />
                <small style={{ fontSize: '12px', color: '#666', marginTop: '5px', display: 'block' }}>
                  계좌번호 앞 8자리 + 뒤 2자리 (예: 12345678-01)
                </small>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    name="is_paper_trading"
                    checked={formData.is_paper_trading}
                    onChange={(e) => setFormData({ ...formData, is_paper_trading: e.target.checked })}
                  />
                  모의투자 사용 (체크 시 모의투자, 미체크 시 실전투자)
                </label>
                <small style={{ fontSize: '12px', color: '#666', marginTop: '5px', display: 'block' }}>
                  모의투자 사용 시 모의투자 앱키/시크릿과 계좌번호를 입력하세요
                </small>
              </div>
            </>
          )}

          <button
            type="submit"
            className="login-submit"
            disabled={loading}
          >
            {loading ? '처리 중...' : (isLogin ? '로그인' : '회원가입')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;

